import { queryOptions, useMutation, useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../client";
import type { Paged, Product, ProductSummary, Store } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";
import type { StoreListParams, StoreProductParams } from "./qk";

// Stores are served by the real server (M2c); responses carry the viewer's `isFollowing`, so every store query
// is reset when the session changes (see session.ts).

export const storeListQuery = (params: StoreListParams = {}) =>
  queryOptions({
    queryKey: qk.stores.list(params),
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.kind) search.set("kind", params.kind);
      if (params.sort) search.set("sort", params.sort);
      if (params.page) search.set("page", String(params.page));
      if (params.pageSize) search.set("pageSize", String(params.pageSize));
      const query = search.toString();
      return apiGet<Paged<Store>>(`/api/stores${query ? `?${query}` : ""}`);
    },
    staleTime: 120_000, // plan §5: catalog list
  });

export const storeQuery = (slug: string) =>
  queryOptions({
    queryKey: qk.stores.detail(slug),
    queryFn: () => apiGet<Store>(`/api/stores/${slug}`),
    staleTime: 300_000, // plan §5: entity detail
  });

export const storeProductsQuery = (slug: string, params: StoreProductParams = {}) =>
  queryOptions({
    queryKey: qk.stores.products(slug, params),
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.q) search.set("q", params.q);
      if (params.category) search.set("category", params.category);
      if (params.sort) search.set("sort", params.sort);
      const query = search.toString();
      return apiGet<ProductSummary[]>(`/api/stores/${slug}/products${query ? `?${query}` : ""}`);
    },
    staleTime: 120_000, // plan §5: catalog list
  });

/** Matched as a prefix, so every cached sort/page variant is patched at once. */
const STORE_LIST_PREFIX = ["stores", "list"] as const;
const STUDIO_ROW_PREFIX = ["stores", "studio"] as const;
const PRODUCT_DETAIL_PREFIX = ["products", "detail"] as const;

function withFollowState(store: Store, following: boolean): Store {
  if (store.isFollowing === following) return store;
  return { ...store, isFollowing: following, followers: store.followers + (following ? 1 : -1) };
}

function patchStore(slug: string, following: boolean) {
  return (previous: Store | undefined): Store | undefined =>
    previous && previous.slug === slug ? withFollowState(previous, following) : previous;
}

function patchStoreList(slug: string, following: boolean) {
  return (previous: Paged<Store> | undefined): Paged<Store> | undefined =>
    previous
      ? { ...previous, items: previous.items.map((store) => (store.slug === slug ? withFollowState(store, following) : store)) }
      : previous;
}

function patchStoreArray(slug: string, following: boolean) {
  return (previous: Store[] | undefined): Store[] | undefined =>
    Array.isArray(previous)
      ? previous.map((store) => (store.slug === slug ? withFollowState(store, following) : store))
      : previous;
}

type ProductWithStore = Product & { store: Store };

function embedsStore(data: unknown, slug: string): data is ProductWithStore {
  return (
    !!data && typeof data === "object" && !Array.isArray(data) && (data as Partial<ProductWithStore>).store?.slug === slug
  );
}

/**
 * The exact product detail keys (`["products", "detail", productSlug]`) whose cached product embeds this store.
 * Never the reviews or related lists nested under them, and never an unrelated product: a follow must not
 * cancel or refetch those.
 */
function productDetailKeysEmbedding(queryClient: QueryClient, slug: string): QueryKey[] {
  return queryClient
    .getQueriesData({ queryKey: PRODUCT_DETAIL_PREFIX })
    .filter(([key, data]) => key.length === qk.products.detail("").length && embedsStore(data, slug))
    .map(([key]) => key);
}

function patchProductStore(following: boolean) {
  return (previous: ProductWithStore | undefined): ProductWithStore | undefined =>
    previous ? { ...previous, store: withFollowState(previous.store, following) } : previous;
}

export interface FollowStoreVariables {
  slug: string;
  following: boolean;
}

/** The follow mutation's options, outside the hook so the cache behaviour is unit-tested. */
export function followStoreMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ slug, following }: FollowStoreVariables) =>
      following
        ? apiPost<{ isFollowing: boolean; followers: number }>(`/api/stores/${slug}/follow`)
        : apiDelete<{ isFollowing: boolean; followers: number }>(`/api/stores/${slug}/follow`),
    ...createOptimisticHandlers<FollowStoreVariables>({
      queryClient,
      action: "follow-store",
      // Plan §5: a follow must update every cached representation of that store — the detail record, its row
      // in any cached store list or studio row, and the store embedded in a product page.
      affectedKeys: ({ slug }) => [qk.stores.detail(slug), STORE_LIST_PREFIX, STUDIO_ROW_PREFIX],
      exactAffectedKeys: ({ slug }) => productDetailKeysEmbedding(queryClient, slug),
      // `exactKeys` is productDetailKeysEmbedding resolved once in onMutate, before the cancel await: a detail
      // cached after that is neither written nor snapshotted, so everything written can be rolled back.
      apply: ({ slug, following }, { exactKeys }) => [
        { queryKey: qk.stores.detail(slug), updater: patchStore(slug, following) },
        { queryKey: STORE_LIST_PREFIX, matchPrefix: true, updater: patchStoreList(slug, following) },
        { queryKey: STUDIO_ROW_PREFIX, matchPrefix: true, updater: patchStoreArray(slug, following) },
        ...exactKeys.map((queryKey) => ({
          queryKey,
          updater: patchProductStore(following),
        })),
      ],
    }),
  };
}

export function useFollowStore() {
  return useMutation(followStoreMutationOptions(useQueryClient()));
}
